import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	type DialogProps,
	Grid2,
	Typography,
} from "@mui/material";
import { useState } from "react";
import type { TaskListArea } from "../models/TaskListArea";
import { setTaskListArea } from "../repositories/TaskListAreasRepository";

type Props = {
	area: TaskListArea | null;
	onHide: (savedMessage?: string) => void;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const RemoveTaskListArea = ({ area, onHide }: Props) => {
	const maxWidth: DialogProps["maxWidth"] = "sm";
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const onSubmit = async () => {
		if (!area) return;
		setIsSubmitLoading(true);
		const resp = await setTaskListArea(area.AssetId, false);
		setIsSubmitLoading(false);

		if (resp.ok) {
			onHide(`${area.AssetName} was removed from the task list areas`);
		} else {
			setError(resp.message);
		}
	};

	const openOrders = area?.OpenWorkOrders ?? 0;

	return (
		<>
			<MesfModal
				title="REMOVE TASK LIST AREA"
				open={!!area}
				maxWidth={maxWidth}
				handleClose={() => onHide()}
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={1}>
						<Grid2 size={12}>
							<Typography>
								Remove <b>{area?.AssetName}</b> from the task list areas?
							</Typography>
							<Typography variant="body2" sx={{ color: "#666" }}>
								{area?.FullPath}
							</Typography>
						</Grid2>
						{area?.AlsoShownIn && (
							<Grid2 size={12} sx={{ mt: 1 }}>
								<Typography variant="body2" color="error">
									It stops showing for everyone whose default area is{" "}
									{area.AlsoShownIn}.
								</Typography>
							</Grid2>
						)}
						<Grid2 size={12}>
							<Typography variant="body2">
								{openOrders === 0
									? "Its section has no open work orders. Assets and work orders are not changed."
									: area?.InsideAreaName
										? `Its ${plural(openOrders, "open work order")} will show in the ${area.InsideAreaName} section instead.`
										: `Its ${plural(openOrders, "open work order")} stop showing on the task list. They stay open on the Work Orders screen.`}
							</Typography>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Grid2 justifyContent="flex-end" size={12} width={"100%"}>
						<Grid2 container justifyContent="flex-end" spacing={1}>
							<Grid2 size={{ md: 3, xs: 5 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide()}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 5 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="error"
									onClick={onSubmit}
								>
									Remove
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
